/**
 * 制作名单场景：电影字幕式自动上滚（↑↓ 调速，Z/X 跳过）。
 * 职员表 + 全部素材署名（线上玩家看不到 docs/）+ 结尾感谢玩家。
 * 通关后自动播放（gameScene 传入 next/params，播完或跳过进结算）。
 * 背景：深色渐变 + 双层视差星空 + 8 个 Kimi 角色正弦上浮。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { CHARACTERS } from '../content/characters.js';
import { t } from '../i18n.js';
import { wrapText } from '../engine/wrapText.js';
import { Assets } from '../engine/assets.js';

/** 确定性伪随机（星空布局，避免每帧重排） */
function prand(i) {
  let x = (i + 1) * 0x9e3779b1;
  x = Math.imul(x ^ (x >>> 15), 1 | x);
  x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
  return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
}

/** 内容行：[类型, i18n键]（h 节标题 / t 正文 / s 空行 / b 大标题） */
const LINES = [
  ['b', 'credits.game'],
  ['s', ''],
  ['s', ''],
  ['h', 'credits.staff'],
  ['t', 'credits.director'],
  ['t', 'credits.rDesign'],
  ['t', 'credits.rLevel'],
  ['t', 'credits.rProg'],
  ['t', 'credits.rMusic'],
  ['t', 'credits.rSfx'],
  ['t', 'credits.rGfx'],
  ['t', 'credits.rQa'],
  ['t', 'credits.rI18n'],
  ['t', 'credits.rReplay'],
  ['s', ''],
  ['h', 'credits.graphics'],
  ['t', 'credits.g1'],
  ['t', 'credits.g1b'],
  ['t', 'credits.g2'],
  ['t', 'credits.g3'],
  ['s', ''],
  ['h', 'credits.music'],
  ['t', 'credits.m1'],
  ['t', 'credits.m2'],
  ['t', 'credits.m3'],
  ['s', ''],
  ['h', 'credits.font'],
  ['t', 'credits.f1'],
  ['s', ''],
  ['h', 'credits.bg'],
  ['t', 'credits.b1'],
  ['t', 'credits.b2'],
  ['s', ''],
  ['h', 'credits.trademark'],
  ['t', 'credits.tm'],
  ['s', ''],
  ['s', ''],
  ['b', 'credits.thanks'],
];

const LINE_H = 26;
const TOP = VIEW_H; // 内容从屏幕底进入（电影字幕式）
const AUTO_SPEED = 18; // px/s 自动上滚
const ADJUST_SPEED = 120; // 按住 ↑↓ 的调速


/** 展开 LINES 为渲染行（'t' 行按宽度断行）。 */
function buildRows() {
  const rows = [];
  for (const [kind, key] of LINES) {
    if (kind === 't') {
      for (const line of wrapText(t(key), VIEW_W - 96, 12)) rows.push(['t', line]); // 署名表全文（maxLines=2 曾静默截断 DCSS 作者名单——历史 bug）
    } else {
      rows.push([kind, key]);
    }
  }
  return rows;
}

export const CreditsScene = {
  mgr: null,

  enter(params) {
    this.t = 0;
    this.scroll = 0; // 自动上滚进度
    this.atEnd = false; // 二次进入不得闪上一局的结束态（审计发现）
    this._rows = buildRows(); // 行构建缓存（update/draw 复用，不再每帧两遍）
    this.next = (params && params.next) || 'title';
    this.nextParams = (params && params.params) || {};
    // 硬等 credits 曲（BGM 懒加载门）：已加载则同步开播；
    // 未加载则 loading 提示、滚动冻结、输入忽略，就绪后开播
    if (Assets.audios.has('bgm_credits')) {
      this.loadingBgm = false;
      this.mgr.shared.audio.playBgm('bgm_credits');
    } else {
      this.loadingBgm = true;
      this.mgr.shared.audio.stopBgm();
      Assets.ensureAudio('bgm_credits').then(() => {
        if (this.mgr.current !== this) return; // 等待期间离开不得在别场景开播（审计发现）
        this.loadingBgm = false;
        this.mgr.shared.audio.playBgm('bgm_credits');
      });
    }
  },

  exit() {},

  update(dt) {
    if (this.loadingBgm) return; // 硬等中：冻结滚动与输入
    this.t += dt;
    const audio = this.mgr.shared.audio;
    const rows = this._rows;
    const total = rows.length * LINE_H + 80;
    // 自动上滚 + ↑↓ 调速
    let speed = AUTO_SPEED;
    if (Input.isDown('down')) speed += ADJUST_SPEED;
    if (Input.isDown('up')) speed -= ADJUST_SPEED;
    this.scroll += speed * dt;
    if (this.scroll < 0) this.scroll = 0;
    // 到底后停住（不自动离开），等按键退出
    if (this.scroll >= total) this.scroll = total;
    this.atEnd = this.scroll >= total;
    if (Input.pressed('confirm') || Input.pressed('cancel')) {
      audio.playSfx('select');
      this.mgr.switchTo(this.next, this.nextParams);
    }
  },

  /** 背景：渐变 + 星空 + 角色上浮 */
  _drawBg(r, assets) {
    r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#04060f' });
    r.rect(0, 0, VIEW_W, VIEW_H / 2, { fill: '#0a1030', alpha: 0.5 });
    for (let layer = 0; layer < 2; layer++) {
      const n = 40;
      const speed = 8 + layer * 10;
      for (let i = 0; i < n; i++) {
        const x = prand(i * 7 + layer * 131) * VIEW_W;
        const y0 = prand(i * 13 + layer * 57) * VIEW_H;
        const y = (y0 + this.t * speed) % VIEW_H;
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(this.t * (1 + layer) + i));
        r.rect(x, y, layer + 1, layer + 1, { fill: '#cdf', alpha: tw * 0.7 });
      }
    }
    for (let i = 0; i < CHARACTERS.length; i++) {
      const img = assets.img(CHARACTERS[i].sprite);
      if (!img) continue;
      const baseX = 40 + i * 58;
      const y = VIEW_H - ((this.t * 12 + i * 90) % (VIEW_H + 120)) + 60;
      const x = baseX + Math.sin(this.t * 0.8 + i * 1.7) * 14;
      r.sprite(img, x, y, { scale: 0.4, alpha: 0.85 });
    }
    r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#000', alpha: 0.45 });
  },

  draw(r) {
    const assets = this.mgr.shared.assets;
    this._drawBg(r, assets);

    if (this.loadingBgm) {
      r.text('LOADING…', VIEW_W / 2, VIEW_H / 2 - 8, { size: 16, color: '#8cf', align: 'center' });
      return;
    }
    r.text(t('credits.hint'), VIEW_W / 2, 16, { size: 11, color: '#889', align: 'center', alpha: 0.8 });
    // 到底后闪烁提示按键退出
    if (this.atEnd && Math.sin(this.t * 4) > -0.2) {
      r.text(t('credits.endHint'), VIEW_W / 2, VIEW_H - 30, { size: 14, color: '#ff4', align: 'center' });
    }

    const rows = this._rows;
    for (let i = 0; i < rows.length; i++) {
      const y = TOP + i * LINE_H - this.scroll;
      if (y < -LINE_H || y > VIEW_H) continue;
      const [kind, val] = rows[i];
      if (kind === 'b') {
        r.text(t(val), VIEW_W / 2, y, { size: 22, color: '#fff', align: 'center' });
      } else if (kind === 'h') {
        r.text(t(val), VIEW_W / 2, y, { size: 15, color: '#fc3', align: 'center' });
      } else if (kind === 't') {
        r.text(val, VIEW_W / 2, y + 2, { size: 12, color: '#dde', align: 'center' });
      }
    }
  },
};
